import axios from "axios";
import moment from "moment";
import { jwtDecode } from "jwt-decode";
import userapiservice from "./userapiservice";
import { store } from "../Reducer/store";
import { setAccessToken } from "../Reducer/localStorageSlice";

/**
 * Returns true when the stored access token is present and not expired
 * (with a 5-minute buffer). Invalid / undecodable tokens return false
 * instead of throwing, so public API calls can still proceed.
 */
const isAccessTokenValid = () => {
  try {
    const token = store?.getState()?.localStorage?.accessToken;
    if (!token) return false;

    const decoded = jwtDecode(token);
    const jwtExpTime = decoded?.exp;
    if (!jwtExpTime) return false;

    const currentTime = moment().add(5, "minutes").unix();
    return currentTime <= jwtExpTime;
  } catch (error) {
    return false;
  }
};

const getToken = async () => {
  try {
    const refreshToken = store?.getState()?.localStorage?.refresh;
    if (refreshToken) {
      const response = await fetch(userapiservice.refreshToken(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refreshToken: refreshToken,
        }),
      });
      const dataR = await response.json();
      if (dataR?.accessToken) {
        store.dispatch(setAccessToken(dataR.accessToken));
        return dataR.accessToken;
      }
    }
  } catch (error) {
    console.error(error);
  }
  return null;
};

axios.interceptors.request.use(
  async (req) => {
    const accessToken = store.getState()?.localStorage?.accessToken;
    if (!accessToken) {
      return req;
    }

    try {
      if (isAccessTokenValid()) {
        req.headers.Authorization = `Bearer ${accessToken}`;
        return req;
      }

      const token = await getToken();
      if (token) {
        req.headers.Authorization = `Bearer ${token}`;
      } else {
        // Drop unusable access tokens so they don't keep breaking requests
        store.dispatch(setAccessToken(""));
      }
      return req;
    } catch (error) {
      console.error(error);
      // Prefer unauthenticated request over failing every API call
      return req;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const postRequest = async (url, data, headers) => {
  try {
    const response = await axios.post(url, data, {
      headers: headers,
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const putRequest = async (url, data, headers) => {
  try {
    const response = await axios.put(url, data, {
      headers: headers,
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

const cache = new Map();
// using 5 mins as default before data is considered stale
const STALE_TIME = 5 * 60 * 1000;

export const getRequest = async (url, headers, dontForceRefresh = true) => {

  // Comment from a developer: Previously it was "forceRefresh" and below if has if(forceRefresh) - it was a mistake i guess
  // Now, i am changing it to "dontForceRefresh" and if(dontForceRefresh) then only it will check for cache and return cached data if not stale, otherwise it will make api call. 
  // If dontForceRefresh is false, then it will always make api call and update cache.
  // I have taken this decision just to avoid changing existing code where getRequest is being called without 3rd param, so by default it will check cache and return cached data if not stale, otherwise it will make api call.

  // console.log('triggered getRequest',url, headers,dontForceRefresh);
  const cachedData = cache.get(encodeURI(url));
  if (dontForceRefresh && cachedData && Date.now() - cachedData.timestamp < STALE_TIME) {
    return Promise.resolve(cachedData.data);
  }

  try {
    const response = await axios.get(url, {
      headers: headers,
    });
    const data = response.data;

    // Store response in cache with timestamp
    // using encoded(url) so endpoints having params with " " will not be seen as new
    cache.set(encodeURI(url), { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.log(error);
    return Promise.reject(error);
  }
};

export const deleteRequest = async (url, data, headers) => {
  try {
    const response = await axios.delete(url, data, {
      headers: headers,
    });
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};
export const API_URL = process.env.REACT_APP_API_BASE_URL;
export const measurementId = process.env.REACT_APP_MEASUREMENT_ID;
